import React, { useState, useEffect, useCallback } from 'react';
import CustomModal from '../CustomModal/CustomModal';
import useSconti from '../../hooks/useSconti';
import { Spinner, Alert, Form, Row, Col, Table, Button, Card, Badge } from 'react-bootstrap';
import { alertService } from '../../services/AlertService';
import { useSelector } from 'react-redux';
import UtilityService from '../../services/UtillityServices/UtilityService';

function ApplicaScontoPartner({ show, onHide, corso, partnerId, onScontoApplied }) {
    
    // Hooks per interagire con le API
    const { 
        getScontoByIdCorso,         // Per il margine di sconto (logica originale)
        getScontiSuperPartner,      // Per la lista campagne
        createScontoSuperPartner,   // Per creare una campagna
        deleteScontoSuperPartner,   // Per eliminare una campagna
        loadingStateSconti 
    } = useSconti();
    
    const userLoggedIn = useSelector(state => state?.user?.userData);
    
    // --- STATO DEL COMPONENTE ---
    const [scontoMassimo, setScontoMassimo] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Stato di caricamento unificato
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        sconto: '',
        data_inizio: '',
        data_fine: '',
        note: ''
    });

    // --- LOGICA DATI ---

    // Funzione unificata per caricare tutti i dati necessari alla modale
    const loadModalData = useCallback(async () => {
        // DEBUG: Stampa i dati dell'utente loggato per verificare il ruolo
        console.log("Dati utente loggato dal componente:", userLoggedIn);

        if (partnerId && corso.id_corso) {
            setIsLoading(true);
            try {
                // Eseguiamo le chiamate in parallelo per ottimizzare i tempi
                const [scontoResult, campaignsResult] = await Promise.all([
                    getScontoByIdCorso({ id_corso: corso.id_corso, id_utente: partnerId }),
                    getScontiSuperPartner({ id_partner: partnerId })
                ]);

                // Impostiamo il margine di sconto massimo
                setScontoMassimo(scontoResult && scontoResult.sconto ? parseFloat(scontoResult.sconto) : 0);

                // Impostiamo e ordiniamo le campagne
                const sortedCampaigns = (campaignsResult || []).sort((a, b) => new Date(b.data_inizio) - new Date(a.data_inizio));
                setCampaigns(sortedCampaigns);

            } catch (error) {
                // Non mostrare alert per errori 403, che sono gestiti come "nessun accesso"
                if (error?.response?.status !== 403) {
                    alertService.error("Errore nel caricamento dei dati per gli sconti.");
                }
            } finally {
                setIsLoading(false);
            }
        }
    }, [partnerId, corso.id_corso, getScontoByIdCorso, getScontiSuperPartner, userLoggedIn]);

    // Carica i dati quando la modale diventa visibile
    useEffect(() => {
        if (show) {
            loadModalData();
            setShowCreateForm(false);
        }
    }, [show, loadModalData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const checkDateOverlap = (newCampaign) => {
        const campaignsForThisCourse = campaigns.filter(c => c.id_corso === corso.id_corso);
        for (const campaign of campaignsForThisCourse) {
            const existingStart = new Date(campaign.data_inizio);
            const existingEnd = new Date(campaign.data_fine);
            const newStart = new Date(newCampaign.data_inizio);
            const newEnd = new Date(newCampaign.data_fine);

            if (newStart <= existingEnd && newEnd >= existingStart) {
                return true;
            }
        }
        return false;
    };

    const handleSaveCampaign = async () => {
        const scontoApplicato = parseFloat(formData.sconto);

        if (isNaN(scontoApplicato) || scontoApplicato <= 0) {
            alertService.warn("Inserire un valore di sconto valido e maggiore di zero.");
            return;
        }

        // VALIDAZIONE: Controlla se lo sconto supera il margine massimo
        if (scontoApplicato > scontoMassimo) {
            alertService.warn(`Lo sconto non può superare il margine massimo di ${scontoMassimo.toFixed(2)} €.`);
            return;
        }

        if (!formData.data_inizio || !formData.data_fine) {
            alertService.warn("Le date di inizio e fine sono obbligatorie.");
            return;
        }
        if (new Date(formData.data_fine) < new Date(formData.data_inizio)) {
            alertService.warn("La data di fine non può essere precedente alla data di inizio.");
            return;
        }
        if (checkDateOverlap(formData)) {
            alertService.error("Le date di questa campagna si sovrappongono con una campagna esistente per lo stesso corso.");
            return;
        }

        const dataToSave = {
            ...formData,
            id_corso: corso.id_corso,
            id_partner: partnerId,
            id_superpartner: userLoggedIn.id_superpartner,
        };

        const success = await createScontoSuperPartner(dataToSave);
        if (success) {
            alertService.success("Campagna sconto creata con successo.");
            setShowCreateForm(false);
            setFormData({ sconto: '', data_inizio: '', data_fine: '', note: '' });
            loadModalData();
            if(onScontoApplied) onScontoApplied();
        }
    };

    const handleDeleteCampaign = async (id_sconto) => {
        if (window.confirm("Sei sicuro di voler eliminare questa campagna?")) {
            const success = await deleteScontoSuperPartner({ id_sconti_corsi_superpartner: id_sconto });
            if (success) {
                alertService.success("Campagna eliminata.");
                loadModalData();
                if(onScontoApplied) onScontoApplied();
            }
        }
    };

    // --- FUNZIONI DI RENDERING ---

    const getCampaignStatus = (campaign) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalizza a mezzanotte per un confronto corretto
        const startDate = new Date(campaign.data_inizio);
        const endDate = new Date(campaign.data_fine);

        if (endDate < today) {
            return { text: 'Scaduta', variant: 'secondary' };
        }
        if (startDate > today) {
            return { text: 'Programmata', variant: 'primary' };
        }
        return { text: 'Attiva', variant: 'success' };
    };

    const renderCampaignsTable = () => {
        const campaignsForCourse = campaigns.filter(c => c.id_corso === corso.id_corso);

        if (isLoading) return <div className="text-center p-3"><Spinner animation="border" /></div>;

        if (campaignsForCourse.length === 0) {
            return <Alert variant="secondary" className="mt-3">Nessuna campagna sconto trovata per questo corso.</Alert>;
        }

        return (
            <Table striped bordered hover responsive size="sm" className="mt-3">
                <thead>
                    <tr>
                        <th>Stato</th>
                        <th>Sconto (€)</th>
                        <th>Periodo Validità</th>
                        <th className="text-center">Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    {campaignsForCourse.map(c => {
                        const status = getCampaignStatus(c);
                        return (
                            <tr key={c.id_sconti_corsi_superpartner} className={status.text === 'Scaduta' ? 'text-muted' : ''}>
                                <td><Badge bg={status.variant}>{status.text}</Badge></td>
                                <td>{parseFloat(c.sconto).toFixed(2)} €</td>
                                <td>Dal {UtilityService.formatDate(c.data_inizio)} al {UtilityService.formatDate(c.data_fine)}</td>
                                <td className="text-center">
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteCampaign(c.id_sconti_corsi_superpartner)} disabled={loadingStateSconti.isLoading}>
                                        <i className="bi bi-trash3"></i>
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const renderCreateForm = () => (
        <Card className="mt-4 bg-light">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Crea Nuova Campagna</h6>
                    <Button variant="close" onClick={() => setShowCreateForm(false)} aria-label="Annulla" />
                </div>
                <hr/>
                <Form>
                     <Form.Group as={Row} className="mb-3"><Form.Label column sm={3}>Sconto (€)</Form.Label><Col sm={9}><Form.Control type="number" name="sconto" value={formData.sconto} onChange={handleInputChange} placeholder={`Max ${scontoMassimo?.toFixed(2) || '0.00'} €`} /></Col></Form.Group>
                     <Form.Group as={Row} className="mb-3"><Form.Label column sm={3}>Data Inizio</Form.Label><Col sm={9}><Form.Control type="date" name="data_inizio" value={formData.data_inizio} onChange={handleInputChange} /></Col></Form.Group>
                     <Form.Group as={Row} className="mb-3"><Form.Label column sm={3}>Data Fine</Form.Label><Col sm={9}><Form.Control type="date" name="data_fine" value={formData.data_fine} onChange={handleInputChange} /></Col></Form.Group>
                     <Form.Group className="mb-3"><Form.Label>Note (Opzionali)</Form.Label><Form.Control as="textarea" rows={2} name="note" value={formData.note} onChange={handleInputChange} /></Form.Group>
                     <div className="text-end">
                         <Button variant="secondary" className="me-2" onClick={() => setShowCreateForm(false)}>Annulla</Button>
                         <Button variant="success" onClick={handleSaveCampaign} disabled={loadingStateSconti.isLoading}>Salva Campagna</Button>
                     </div>
                </Form>
            </Card.Body>
        </Card>
    );

    const renderBody = () => {
        if (isLoading) {
            return <div className="text-center p-5"><Spinner animation="border" /><p className="mt-2">Caricamento dati...</p></div>;
        }

        const canCreateCampaign = scontoMassimo !== null && scontoMassimo > 0;

        return (
            <div>
                <p>Stai gestendo le campagne sconto per il corso: <strong>{corso?.denominazione}</strong>.</p>
                {canCreateCampaign ? (
                    <Alert variant="info">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        Il tuo margine di sconto massimo per questo corso è di <strong>{scontoMassimo.toFixed(2)} €</strong>.
                    </Alert>
                ) : (
                    <Alert variant="warning">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Nessun margine di sconto è stato assegnato per questo corso. Non puoi creare nuove campagne.
                    </Alert>
                )}
                <hr/>
                {showCreateForm ? (
                    canCreateCampaign ? renderCreateForm() : null
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Storico Campagne</h5>
                            {canCreateCampaign && (
                                <Button variant="primary" onClick={() => setShowCreateForm(true)}>
                                    <i className="bi bi-plus-circle me-2"></i>Nuova Campagna
                                </Button>
                            )}
                        </div>
                        {renderCampaignsTable()}
                    </>
                )}
            </div>
        );
    };

    return (
        <CustomModal
            show={show}
            onHide={onHide}
            headerText="Gestione Sconti e Campagne"
            body={renderBody()}
            hideSaveBtn={true} 
            size="lg"
            isLoading={loadingStateSconti.isLoading}
            onUndoChanges={onHide}
        />
    );
}

export default ApplicaScontoPartner;
